import { useState, useEffect, useCallback } from 'react';
import { useSession } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import {
  StatisticsFilters,
  StatisticsData,
  StatisticsLoadingState,
  DashboardStats,
  ThroughputMetrics,
  PhaseDuration,
  TimeSeriesData,
  Distributions,
  ActionItems,
  HomeDashboardSummary,
  ExtendedDistributionFilters,
  ExtendedDistributions,
  ExpenseFilters,
  ExpenseDistributions,
  TopTenantsFilters,
  TopTenants,
  BuildingBacklogEvolution,
} from '../types/statistics.types';

export function useStatisticsData(filters: StatisticsFilters = {}) {
  const { session } = useSession();

  const [data, setData] = useState<StatisticsData>({
    dashboard: null,
    throughput: null,
    phaseDurations: null,
    timeSeries: null,
    distributions: null,
    actionItems: null,
  });

  const [loading, setLoading] = useState<StatisticsLoadingState>({
    dashboard: true,
    throughput: true,
    phaseDurations: true,
    timeSeries: true,
    distributions: true,
    actionItems: true,
  });

  const [error, setError] = useState<string | null>(null);

  // Build query string from filters
  const buildQueryString = useCallback((additionalFilters: Record<string, string> = {}) => {
    const params = new URLSearchParams();
    if (filters.buildingId) params.append('buildingId', filters.buildingId);
    if (filters.label) params.append('label', filters.label);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    Object.entries(additionalFilters).forEach(([key, value]) => {
      params.append(key, value);
    });
    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }, [filters]);

  // Fetch dashboard stats
  const fetchDashboard = useCallback(async () => {
    if (!session) return;

    try {
      setLoading(prev => ({ ...prev, dashboard: true }));
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/statistics/dashboard${buildQueryString()}`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result: DashboardStats = await response.json();
      setData(prev => ({ ...prev, dashboard: result }));
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setError('Fout bij ophalen van dashboard statistieken');
    } finally {
      setLoading(prev => ({ ...prev, dashboard: false }));
    }
  }, [session, buildQueryString]);

  // Fetch throughput metrics
  const fetchThroughput = useCallback(async () => {
    if (!session) return;

    try {
      setLoading(prev => ({ ...prev, throughput: true }));
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/statistics/throughput${buildQueryString()}`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result: ThroughputMetrics = await response.json();
      setData(prev => ({ ...prev, throughput: result }));
    } catch (err) {
      console.error('Error fetching throughput metrics:', err);
    } finally {
      setLoading(prev => ({ ...prev, throughput: false }));
    }
  }, [session, buildQueryString]);

  // Fetch phase durations
  const fetchPhaseDurations = useCallback(async () => {
    if (!session) return;

    try {
      setLoading(prev => ({ ...prev, phaseDurations: true }));
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/statistics/phase-durations${buildQueryString()}`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result: PhaseDuration[] = await response.json();
      setData(prev => ({ ...prev, phaseDurations: result }));
    } catch (err) {
      console.error('Error fetching phase durations:', err);
    } finally {
      setLoading(prev => ({ ...prev, phaseDurations: false }));
    }
  }, [session, buildQueryString]);

  // Fetch time series data
  const fetchTimeSeries = useCallback(async () => {
    if (!session) return;

    try {
      setLoading(prev => ({ ...prev, timeSeries: true }));
      const granularity = filters.granularity || 'week';
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/statistics/time-series${buildQueryString({ granularity })}`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result: TimeSeriesData = await response.json();
      setData(prev => ({ ...prev, timeSeries: result }));
    } catch (err) {
      console.error('Error fetching time series:', err);
    } finally {
      setLoading(prev => ({ ...prev, timeSeries: false }));
    }
  }, [session, buildQueryString, filters.granularity]);

  // Fetch distributions
  const fetchDistributions = useCallback(async () => {
    if (!session) return;

    try {
      setLoading(prev => ({ ...prev, distributions: true }));
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/statistics/distributions`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result: Distributions = await response.json();
      setData(prev => ({ ...prev, distributions: result }));
    } catch (err) {
      console.error('Error fetching distributions:', err);
    } finally {
      setLoading(prev => ({ ...prev, distributions: false }));
    }
  }, [session]);

  // Fetch action items
  const fetchActionItems = useCallback(async () => {
    if (!session) return;

    try {
      setLoading(prev => ({ ...prev, actionItems: true }));
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/statistics/action-items`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result: ActionItems = await response.json();
      setData(prev => ({ ...prev, actionItems: result }));
    } catch (err) {
      console.error('Error fetching action items:', err);
    } finally {
      setLoading(prev => ({ ...prev, actionItems: false }));
    }
  }, [session]);

  // Refresh all data
  const refresh = useCallback(() => {
    fetchDashboard();
    fetchThroughput();
    fetchPhaseDurations();
    fetchTimeSeries();
    fetchDistributions();
    fetchActionItems();
  }, [fetchDashboard, fetchThroughput, fetchPhaseDurations, fetchTimeSeries, fetchDistributions, fetchActionItems]);

  // Initial fetch
  useEffect(() => {
    if (session) {
      refresh();
    }
  }, [session, JSON.stringify(filters)]);

  // Check if all data is loading
  const isLoading = Object.values(loading).some(Boolean);

  return {
    data,
    loading,
    isLoading,
    error,
    refresh,
  };
}

// Hook for home dashboard summary only
export function useHomeDashboardSummary() {
  const { session } = useSession();
  const [data, setData] = useState<HomeDashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;

    const fetchSummary = async () => {
      try {
        setLoading(true);
        const response = await fetchWithAuth(
          `${API_URL}/helpdesk/statistics/home-summary`,
          { method: 'GET' },
          session
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result: HomeDashboardSummary = await response.json();
        setData(result);
      } catch (err) {
        console.error('Error fetching home dashboard summary:', err);
        setError('Fout bij ophalen van dashboard samenvatting');
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [session]);

  return { data, loading, error };
}

// Hook for extended distributions with filters
export function useExtendedDistributions(filters: ExtendedDistributionFilters = {}) {
  const { session } = useSession();
  const [data, setData] = useState<ExtendedDistributions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!session) return;

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filters.ticketStatus) params.append('ticketStatus', filters.ticketStatus);
      if (filters.year) params.append('year', filters.year);
      if (filters.month) params.append('month', filters.month);

      const queryString = params.toString() ? `?${params.toString()}` : '';

      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/statistics/extended-distributions${queryString}`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result: ExtendedDistributions = await response.json();
      setData(result);
    } catch (err) {
      console.error('Error fetching extended distributions:', err);
      setError('Fout bij ophalen van uitgebreide verdelingen');
    } finally {
      setLoading(false);
    }
  }, [session, filters.ticketStatus, filters.year, filters.month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refresh: fetchData };
}

/**
 * Uitgaven per gebouw en per label, uit de OPEX-facturen die aan tickets
 * gekoppeld zijn. Jaar/maand filteren op de factuurdatum.
 */
export function useExpenseDistributions(filters: ExpenseFilters = {}) {
  const { session } = useSession();
  const [data, setData] = useState<ExpenseDistributions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!session) return;

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filters.year) params.append('year', filters.year);
      if (filters.month) params.append('month', filters.month);
      const queryString = params.toString() ? `?${params.toString()}` : '';

      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/statistics/expenses${queryString}`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result: ExpenseDistributions = await response.json();
      setData(result);
    } catch (err) {
      console.error('Error fetching expense distributions:', err);
      setError('Fout bij ophalen van uitgaven');
    } finally {
      setLoading(false);
    }
  }, [session, filters.year, filters.month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refresh: fetchData };
}

/** Melders (huurders) met de meeste helpdesktickets. */
export function useTopTenants(filters: TopTenantsFilters = {}) {
  const { session } = useSession();
  const [data, setData] = useState<TopTenants | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!session) return;

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filters.year) params.append('year', filters.year);
      if (filters.month) params.append('month', filters.month);
      if (filters.limit) params.append('limit', String(filters.limit));
      if (filters.onlyTaggedTenants) params.append('onlyTaggedTenants', 'true');
      const queryString = params.toString() ? `?${params.toString()}` : '';

      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/statistics/top-tenants${queryString}`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result: TopTenants = await response.json();
      setData(result);
    } catch (err) {
      console.error('Error fetching top tenants:', err);
      setError('Fout bij ophalen van top huurders');
    } finally {
      setLoading(false);
    }
  }, [
    session,
    filters.year,
    filters.month,
    filters.limit,
    filters.onlyTaggedTenants,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refresh: fetchData };
}

// Hook for building backlog evolution (time series of open tickets per building)
export function useBuildingBacklogEvolution(granularity: 'week' | 'month' = 'week', source?: string) {
  const { session } = useSession();
  const [data, setData] = useState<BuildingBacklogEvolution | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!session) return;

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({ granularity });
      if (source) {
        params.append('source', source);
      }

      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/statistics/building-backlog-evolution?${params.toString()}`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result: BuildingBacklogEvolution = await response.json();
      setData(result);
    } catch (err) {
      console.error('Error fetching building backlog evolution:', err);
      setError('Fout bij ophalen van gebouw backlog evolutie');
    } finally {
      setLoading(false);
    }
  }, [session, granularity, source]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refresh: fetchData };
}
